import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Zap, ArrowLeft, Check, X, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Header from './Header';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OpenAiSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);

  // Settings state
  const [settings, setSettings] = useState(null);
  const [purposes, setPurposes] = useState({});
  const [defaultModels, setDefaultModels] = useState({});
  const [availableModels, setAvailableModels] = useState([]);

  // Form state (apiKey is a candidate — empty means "unchanged")
  const [apiKey, setApiKey] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [models, setModels] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/openai-config`, authHeaders());
      setSettings(resp.data.settings);
      setPurposes(resp.data.purposes || {});
      setDefaultModels(resp.data.default_models || {});
      setAvailableModels(resp.data.available_models || []);
      setBaseUrl(resp.data.settings.base_url || '');
      setModels(resp.data.settings.models || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load OpenAI settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        // null = unchanged; explicit "" clears; value updates
        api_key: apiKey === '' ? null : apiKey,
        organization_id: organizationId === '' ? null : organizationId,
        base_url: baseUrl || '',
        models: models,
      };
      await axios.put(`${API}/admin/openai-config`, payload, authHeaders());
      toast.success('OpenAI settings saved');
      setApiKey('');
      setOrganizationId('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {};
      if (apiKey) payload.api_key = apiKey;
      if (baseUrl) payload.base_url = baseUrl;
      const resp = await axios.post(`${API}/admin/openai-config/test`, payload, authHeaders());
      setTestResult(resp.data);
      if (resp.data.ok) toast.success('OpenAI key is valid ✓');
      else toast.error(`Test failed: ${resp.data.error?.slice(0, 80) || 'unknown'}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const updateModel = (purpose, val) => setModels((m) => ({ ...m, [purpose]: val }));
  const clearApiKey = () => setApiKey(''); // eslint-disable-line no-unused-vars

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="outline"
          onClick={() => navigate('/admin')}
          className="mb-6"
          data-testid="openai-settings-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin
        </Button>

        <div className="mb-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">OpenAI Settings</h1>
            <p className="text-sm text-slate-600">
              Centralised configuration for every AI feature across the platform.
            </p>
          </div>
        </div>

        {/* Credentials */}
        <Card className="mb-6 border-slate-200">
          <CardHeader>
            <CardTitle>Credentials</CardTitle>
            <CardDescription>
              Stored encrypted in the database. Applied immediately — no restart required.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="api-key">
                API Key
                {settings?.api_key_set && (
                  <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200">
                    <Check className="w-3 h-3 mr-1" />
                    Configured ({settings.api_key_preview})
                  </Badge>
                )}
              </Label>
              <div className="mt-1 flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="api-key"
                    type={showKey ? 'text' : 'password'}
                    placeholder={settings?.api_key_set ? '••••••••  (leave blank to keep current key)' : 'sk-proj-…'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="font-mono pr-10"
                    data-testid="openai-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={testConnection}
                  disabled={testing || (!apiKey && !settings?.api_key_set)}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="openai-test-btn"
                >
                  {testing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testing…</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" />Test Connection</>
                  )}
                </Button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get a key from
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 text-indigo-600 hover:underline inline-flex items-center gap-0.5"
                >
                  platform.openai.com/api-keys <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              {testResult && (
                <div className={`mt-3 rounded-md p-3 text-sm ${
                  testResult.ok
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                    : 'bg-red-50 border border-red-200 text-red-900'
                }`} data-testid="openai-test-result">
                  {testResult.ok ? (
                    <>
                      <p className="font-medium flex items-center gap-2">
                        <Check className="w-4 h-4" /> Key is valid.
                      </p>
                      {testResult.sample_models?.length > 0 && (
                        <p className="mt-1 text-xs text-emerald-700">
                          Available models (sample): {testResult.sample_models.slice(0, 8).join(', ')}
                          {testResult.total_reported > 8 && ` +${testResult.total_reported - 8} more`}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="font-medium flex items-center gap-2">
                      <X className="w-4 h-4" /> {testResult.error}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org-id">Organization ID <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  id="org-id"
                  placeholder={settings?.organization_id_set ? '••••  (already set)' : 'org-…'}
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className="mt-1 font-mono"
                />
                <p className="mt-1 text-xs text-slate-500">Useful if you belong to multiple OpenAI orgs.</p>
              </div>

              <div>
                <Label htmlFor="base-url">Base URL <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  id="base-url"
                  placeholder="https://api.openai.com/v1"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="mt-1 font-mono"
                />
                <p className="mt-1 text-xs text-slate-500">For Azure OpenAI or an OpenAI-compatible proxy.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Model selection per purpose */}
        <Card className="mb-6 border-slate-200">
          <CardHeader>
            <CardTitle>Model selection</CardTitle>
            <CardDescription>
              Choose which OpenAI model powers each part of the platform. Defaults are used if you leave a field blank.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(purposes).map(([purposeKey, label]) => {
              const current = models[purposeKey] || '';
              const def = defaultModels[purposeKey] || defaultModels.default;
              return (
                <div key={purposeKey} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                  <div className="md:col-span-2">
                    <Label htmlFor={`model-${purposeKey}`} className="text-sm font-medium text-slate-900">
                      {label}
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Default: <code className="bg-slate-100 px-1 rounded">{def}</code>
                    </p>
                  </div>
                  <Select
                    value={current || def}
                    onValueChange={(v) => updateModel(purposeKey, v)}
                  >
                    <SelectTrigger id={`model-${purposeKey}`} className="w-full" data-testid={`openai-model-${purposeKey}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between sticky bottom-4 bg-white/80 backdrop-blur border border-slate-200 rounded-lg px-4 py-3 shadow-sm">
          <p className="text-xs text-slate-500">
            {settings?.updated_at ? (
              <>Last updated {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by_email || 'admin'}</>
            ) : (
              <>No settings saved yet.</>
            )}
          </p>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="openai-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}
